/**
 * User Behavior Event Tracker
 *
 * Kritik kullanıcı eventlerini Firestore'a loglar.
 * GA4'ü (analytics.ts) tamamlayıcı - user-level analytics ve AI insights için.
 *
 * Kullanım:
 * - eventTracker.startSession(userId)
 * - eventTracker.trackPremiumFunnel(userId, 'modal_view', { source: 'header' })
 * - eventTracker.trackFeature(userId, 'tool_video_search', { query: '...' })
 *
 * @see USER_BEHAVIOR_ANALYTICS_ROADMAP.md
 */

import {
  collection,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';

// ============================================================================
// TYPES
// ============================================================================

export type EventCategory =
  | 'session'      // login, logout, session_start, session_end
  | 'premium'      // modal_view, page_view, checkout_start, purchase
  | 'feature'      // tool_use, media_upload, message_sent
  | 'milestone'    // first_message, first_return, streak_3
  | 'sanayi';      // sanayi raporu funnel events

export type SessionEventType =
  | 'session_start'
  | 'session_end'
  | 'login'
  | 'logout';

export type PremiumEventType =
  | 'premium_modal_view'
  | 'premium_modal_cta_click'
  | 'premium_page_view'
  | 'premium_checkout_start'
  | 'premium_purchase';

export type FeatureEventType =
  | 'message_sent'
  | 'conversation_created'
  | 'tool_video_search'
  | 'tool_price_search'
  | 'tool_web_search'
  | 'media_upload_image'
  | 'media_upload_video'
  | 'media_upload_audio';

export type MilestoneEventType =
  | 'milestone_first_message'
  | 'milestone_first_tool'
  | 'milestone_first_return'
  | 'milestone_streak_3'
  | 'milestone_streak_7';

export type SanayiEventType =
  | 'sanayi_session_created'      // createSanayiRaporuSession başarılı
  | 'sanayi_first_message'        // İlk bilgiToplama mesajı gönderildi
  | 'sanayi_pattern_ready'        // Pattern ≥ %70 → CloserPrompt tetiklendi
  | 'sanayi_final_confirmation'   // FinalConfirmation modal açıldı
  | 'sanayi_ikna_view'            // IknaScreen görüntülendi
  | 'sanayi_ikna_cta_click'       // IknaScreen'de CTA'ya tıklandı (satın al / görüntüle)
  | 'sanayi_report_view'          // Rapor tam görüntülendi (ikna bariyeri geçildi)
  | 'sanayi_checkout_start'       // Sanayi kredi checkout açıldı
  | 'sanayi_checkout_success'     // Sanayi kredi ödeme başarılı
  | 'sanayi_checkout_fail'        // Sanayi kredi ödeme başarısız
  | 'sanayi_niyet_beyani_devam'   // Niyet beyanı gate: "Evet devam et" tıklandı
  | 'sanayi_niyet_beyani_kapat';  // Niyet beyanı gate: backdrop ile kapatıldı

export type EventType =
  | SessionEventType
  | PremiumEventType
  | FeatureEventType
  | MilestoneEventType
  | SanayiEventType;

interface EventData {
  [key: string]: string | number | boolean | undefined;
}

interface UserEvent {
  type: EventType;
  category: EventCategory;
  timestamp: ReturnType<typeof serverTimestamp>;
  sessionId?: string;
  data?: EventData;
}

// ============================================================================
// STATE
// ============================================================================

let currentSessionId: string | null = null;

// ============================================================================
// CORE FUNCTION
// ============================================================================

/**
 * Generic event logging to Firestore
 * Silent fail - analytics should never break the app
 */
async function logEvent(
  userId: string,
  type: EventType,
  category: EventCategory,
  data?: EventData
): Promise<void> {
  if (!userId) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[EventTracker] No userId, skipping event:', type);
    }
    return;
  }

  try {
    const eventsRef = collection(db, 'users', userId, 'events');

    const event: UserEvent = {
      type,
      category,
      timestamp: serverTimestamp(),
      ...(currentSessionId && { sessionId: currentSessionId }),
      ...(data && Object.keys(data).length > 0 && { data }),
    };

    await addDoc(eventsRef, event);

    if (process.env.NODE_ENV === 'development') {
      console.log('[EventTracker]', type, data || '');
    }
  } catch (error) {
    // Silent fail - analytics shouldn't break the app
    if (process.env.NODE_ENV === 'development') {
      console.error('[EventTracker] Error logging event:', error);
    }
  }
}

// ============================================================================
// SESSION TRACKING
// ============================================================================

function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export const eventTracker = {
  /**
   * Get current session ID (for debugging)
   */
  getSessionId: () => currentSessionId,

  /**
   * Session başlat (login veya app mount)
   */
  startSession: (userId: string) => {
    currentSessionId = generateSessionId();
    logEvent(userId, 'session_start', 'session');
  },

  /**
   * Session bitir (logout veya tab close)
   */
  endSession: (userId: string) => {
    if (currentSessionId) {
      logEvent(userId, 'session_end', 'session');
      currentSessionId = null;
    }
  },

  /**
   * Login event (session_start'tan ayrı - method tracking için)
   */
  trackLogin: (userId: string, method: 'google' | 'apple' | 'email') => {
    logEvent(userId, 'login', 'session', { method });
  },

  /**
   * Logout event
   */
  trackLogout: (userId: string) => {
    logEvent(userId, 'logout', 'session');
  },

  // ============================================================================
  // PREMIUM FUNNEL
  // ============================================================================

  /**
   * Premium modal görüntüleme
   * @param source - Modal'ın nereden açıldığı (limit_reached, master_mode, sidebar, etc.)
   */
  trackPremiumModalView: (userId: string, source: string) => {
    logEvent(userId, 'premium_modal_view', 'premium', { source });
  },

  /**
   * Premium modal CTA tıklama ("Premium'a Geç" butonu)
   */
  trackPremiumModalCTA: (userId: string, source: string) => {
    logEvent(userId, 'premium_modal_cta_click', 'premium', { source });
  },

  /**
   * Premium sayfa görüntüleme (/premium)
   */
  trackPremiumPageView: (userId: string, referrer?: string) => {
    logEvent(userId, 'premium_page_view', 'premium', referrer ? { referrer } : undefined);
  },

  /**
   * Checkout başlatma (Dodopayments'a yönlendirildi)
   */
  trackCheckoutStart: (userId: string) => {
    logEvent(userId, 'premium_checkout_start', 'premium');
  },

  /**
   * Satın alma tamamlandı (webhook'tan backend tarafından çağrılabilir)
   */
  trackPurchase: (userId: string, amount?: number) => {
    logEvent(userId, 'premium_purchase', 'premium', amount ? { amount } : undefined);
  },

  // ============================================================================
  // FEATURE USAGE
  // ============================================================================

  /**
   * Mesaj gönderildi
   */
  trackMessageSent: (userId: string, conversationId: string) => {
    logEvent(userId, 'message_sent', 'feature', { conversationId });
  },

  /**
   * Yeni konuşma oluşturuldu
   */
  trackConversationCreated: (userId: string, category: string) => {
    logEvent(userId, 'conversation_created', 'feature', { category });
  },

  /**
   * Tool kullanımı (video/price/web search)
   */
  trackToolUse: (
    userId: string,
    toolType: 'video' | 'price' | 'web',
    query?: string
  ) => {
    const eventType: FeatureEventType = `tool_${toolType}_search` as FeatureEventType;
    logEvent(userId, eventType, 'feature', query ? { query: query.substring(0, 100) } : undefined);
  },

  /**
   * Medya yükleme (image/video/audio)
   */
  trackMediaUpload: (
    userId: string,
    mediaType: 'image' | 'video' | 'audio',
    sizeMB?: number
  ) => {
    const eventType: FeatureEventType = `media_upload_${mediaType}` as FeatureEventType;
    logEvent(userId, eventType, 'feature', sizeMB ? { sizeMB } : undefined);
  },

  // ============================================================================
  // MILESTONES
  // ============================================================================

  /**
   * İlk mesaj gönderildi (activation)
   */
  trackFirstMessage: (userId: string) => {
    logEvent(userId, 'milestone_first_message', 'milestone');
  },

  /**
   * İlk tool kullanıldı (feature discovery)
   */
  trackFirstToolUse: (userId: string) => {
    logEvent(userId, 'milestone_first_tool', 'milestone');
  },

  /**
   * İlk kez geri geldi (retention signal)
   */
  trackFirstReturn: (userId: string) => {
    logEvent(userId, 'milestone_first_return', 'milestone');
  },

  /**
   * 3 gün üst üste aktif (habit forming)
   */
  trackStreak3: (userId: string) => {
    logEvent(userId, 'milestone_streak_3', 'milestone');
  },

  /**
   * 7 gün üst üste aktif (strong retention)
   */
  trackStreak7: (userId: string) => {
    logEvent(userId, 'milestone_streak_7', 'milestone');
  },

  // ============================================================================
  // SANAYI RAPORU FUNNEL
  // ============================================================================

  /** Yeni sanayi oturumu oluşturuldu */
  trackSanayiSessionCreated: (userId: string, conversationId: string) => {
    logEvent(userId, 'sanayi_session_created', 'sanayi', { conversationId });
  },

  /** İlk bilgiToplama mesajı gönderildi (aktivasyon) */
  trackSanayiFirstMessage: (userId: string, conversationId: string) => {
    logEvent(userId, 'sanayi_first_message', 'sanayi', { conversationId });
  },

  /** Pattern doluluk ≥70 eşiği geçildi */
  trackSanayiPatternReady: (userId: string, conversationId: string, patternScore: number) => {
    logEvent(userId, 'sanayi_pattern_ready', 'sanayi', { conversationId, patternScore });
  },

  /** FinalConfirmation modalı açıldı */
  trackSanayiFinalConfirmation: (userId: string, conversationId: string) => {
    logEvent(userId, 'sanayi_final_confirmation', 'sanayi', { conversationId });
  },

  /** İkna ekranı görüntülendi */
  trackSanayiIknaView: (
    userId: string,
    conversationId: string,
    mode: 'no_credit' | 'has_credit' | 'free',
    aciNoktasi?: string
  ) => {
    logEvent(userId, 'sanayi_ikna_view', 'sanayi', {
      conversationId,
      mode,
      ...(aciNoktasi && { aciNoktasi }),
    });
  },

  /** İkna ekranındaki CTA'ya tıklandı (Satın Al veya Raporu Görüntüle) */
  trackSanayiIknaCTA: (userId: string, conversationId: string, ctaType: 'buy' | 'view') => {
    logEvent(userId, 'sanayi_ikna_cta_click', 'sanayi', { conversationId, ctaType });
  },

  /** Rapor tam içeriği görüntülendi (ikna bariyeri geçildi) */
  trackSanayiReportView: (userId: string, conversationId: string, method: 'credit' | 'free' | 'already_unlocked') => {
    logEvent(userId, 'sanayi_report_view', 'sanayi', { conversationId, method });
  },

  /** Sanayi kredi checkout açıldı */
  trackSanayiCheckoutStart: (userId: string, conversationId: string, paketId?: string) => {
    logEvent(userId, 'sanayi_checkout_start', 'sanayi', {
      conversationId,
      ...(paketId && { paketId }),
    });
  },

  /** Sanayi kredi ödeme başarılı */
  trackSanayiCheckoutSuccess: (userId: string, conversationId: string, paketId?: string) => {
    logEvent(userId, 'sanayi_checkout_success', 'sanayi', {
      conversationId,
      ...(paketId && { paketId }),
    });
  },

  /** Sanayi kredi ödeme başarısız */
  trackSanayiCheckoutFail: (userId: string, conversationId: string) => {
    logEvent(userId, 'sanayi_checkout_fail', 'sanayi', { conversationId });
  },

  /** Niyet beyanı gate: "Evet devam et" tıklandı */
  trackSanayiNiyetBeyaniDevam: (userId: string, conversationId: string, patternScore: number) => {
    logEvent(userId, 'sanayi_niyet_beyani_devam', 'sanayi', { conversationId, patternScore });
  },

  /** Niyet beyanı gate: backdrop ile kapatıldı */
  trackSanayiNiyetBeyaniKapat: (userId: string, conversationId: string) => {
    logEvent(userId, 'sanayi_niyet_beyani_kapat', 'sanayi', { conversationId });
  },

  // ============================================================================
  // GENERIC
  // ============================================================================

  /**
   * Generic event logging (for custom events)
   */
  track: (
    userId: string,
    type: EventType,
    category: EventCategory,
    data?: EventData
  ) => {
    logEvent(userId, type, category, data);
  },
};

// Export types for external use
export type { EventData };
